import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Heart } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

function Shell({ title, sub, children, testid }) {
  return (
    <div className="min-h-screen bg-[var(--bg)] flex items-center justify-center px-6 py-12" data-testid={testid}>
      <div className="w-full max-w-sm">
        <Heart className="w-6 h-6 text-[var(--gold)] mb-4" />
        <p className="text-[11px] uppercase tracking-[0.25em] text-[var(--gold)]">Guest portal</p>
        <h1 className="font-serif font-light text-4xl mt-1 mb-2">{title}</h1>
        {sub && <p className="text-sm text-[var(--muted)] mb-6">{sub}</p>}
        {children}
      </div>
    </div>
  );
}

function useSubmit(fn) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function run(e) {
    e.preventDefault(); setBusy(true); setError("");
    try { await fn(); } catch (err) { setError(formatApiError(err)); } finally { setBusy(false); }
  }
  return { busy, error, run };
}

const Err = ({ msg }) => (msg ? <p className="text-sm text-red-600" data-testid="guest-auth-error">{msg}</p> : null);

export function GuestLogin() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [f, setF] = useState({ email: "", password: "" });
  const { busy, error, run } = useSubmit(async () => {
    await api.post("/guest-auth/login", f);
    if (sp.get("slug") && sp.get("g")) await api.post("/guest-auth/link", { slug: sp.get("slug"), guest_id: sp.get("g") }).catch((e) => toast.error(`We couldn't add that wedding: ${formatApiError(e)}`));
    nav("/guest", { replace: true });
  });
  return (
    <Shell title="Welcome back" sub="Log in to see your RSVP, table, photos and messages with the couple." testid="guest-login-page">
      <form onSubmit={run} className="space-y-3">
        <input type="email" required placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="input-luxe" data-testid="guest-login-email" />
        <input type="password" required placeholder="Password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="input-luxe" data-testid="guest-login-password" />
        <Err msg={error} />
        <button disabled={busy} className="btn-gold w-full" data-testid="guest-login-submit">{busy ? "Logging in…" : "Log in"}</button>
      </form>
      <div className="flex justify-between text-xs mt-4 text-[var(--muted)]">
        <Link to="/guest/forgot" className="underline" data-testid="guest-forgot-link">Forgot password?</Link>
        <span>New? Use the personal link in your invitation.</span>
      </div>
    </Shell>
  );
}

export function GuestSignup() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const slug = sp.get("slug"), g = sp.get("g");
  const [f, setF] = useState({ name: sp.get("name") || "", email: "", password: "" });
  const { busy, error, run } = useSubmit(async () => { await api.post("/guest-auth/register", { ...f, slug, guest_id: g }); toast.success("Your guest portal is ready"); nav("/guest", { replace: true }); });
  if (!slug || !g) return <Shell title="Open your invitation link" sub="Guest accounts are created from the personal RSVP link the couple sent you, so we know which wedding you belong to." testid="guest-signup-missing"><Link to="/guest/login" className="btn-outline-gold w-full">I already have an account</Link></Shell>;
  return (
    <Shell title="Create your guest login" sub="One place for your RSVP, seat, schedule, photos and a private chat with the couple." testid="guest-signup-page">
      <form onSubmit={run} className="space-y-3">
        <input placeholder="Your name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="input-luxe" data-testid="guest-signup-name" />
        <input type="email" required placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="input-luxe" data-testid="guest-signup-email" />
        <input type="password" required minLength={8} placeholder="Password (8+ characters)" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="input-luxe" data-testid="guest-signup-password" />
        <Err msg={error} />
        <button disabled={busy} className="btn-gold w-full" data-testid="guest-signup-submit">{busy ? "Creating…" : "Create my login"}</button>
      </form>
      <p className="text-xs mt-4 text-[var(--muted)]">Already have a guest login? <Link to={`/guest/login?slug=${slug}&g=${g}`} className="underline" data-testid="guest-signup-login-link">Log in to add this wedding</Link></p>
    </Shell>
  );
}

export function GuestForgot() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState("");
  const { busy, error, run } = useSubmit(async () => { const { data } = await api.post("/guest-auth/forgot-password", { email, origin_url: window.location.origin }); setSent(data.message); });
  return (
    <Shell title="Reset password" sub="We'll email you a link to choose a new password." testid="guest-forgot-page">
      {sent ? <p className="text-sm" data-testid="guest-forgot-sent">{sent}</p> : (
        <form onSubmit={run} className="space-y-3">
          <input type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="input-luxe" data-testid="guest-forgot-email" />
          <Err msg={error} />
          <button disabled={busy} className="btn-gold w-full" data-testid="guest-forgot-submit">Send reset link</button>
        </form>
      )}
      <Link to="/guest/login" className="text-xs underline text-[var(--muted)] mt-4 inline-block">Back to log in</Link>
    </Shell>
  );
}

export function GuestReset() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [password, setPassword] = useState("");
  const { busy, error, run } = useSubmit(async () => { await api.post("/guest-auth/reset-password", { token: sp.get("token") || "", password }); toast.success("Password updated. Please log in."); nav("/guest/login"); });
  return (
    <Shell title="Choose a new password" testid="guest-reset-page">
      <form onSubmit={run} className="space-y-3">
        <input type="password" required minLength={8} placeholder="New password (8+ characters)" value={password} onChange={(e) => setPassword(e.target.value)} className="input-luxe" data-testid="guest-reset-password" />
        <Err msg={error} />
        <button disabled={busy} className="btn-gold w-full" data-testid="guest-reset-submit">Save password</button>
      </form>
    </Shell>
  );
}

export function useGuest() {
  const [me, setMe] = useState(null);
  useEffect(() => {
    api.get("/guest-auth/me").catch(() => api.post("/guest-auth/refresh").then(() => api.get("/guest-auth/me")))
      .then((r) => setMe(r.data)).catch(() => setMe(false));
  }, []);
  return me;
}
